import React, { ReactNode } from 'react';
import { Box } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { LogoMark, Mono, useModeTokens } from '@/common/components/ui';
import { fonts } from '@/theme';

interface LegalPageLayoutProps {
  eyebrow: string;
  title: string;
  summary: string;
  children: ReactNode;
  dateLabel?: string;
}

const legalLinkSx = {
  color: 'inherit',
  textDecorationColor: 'currentColor',
  textUnderlineOffset: '3px',
  '&:hover': { textDecorationThickness: '2px' },
} as const;

export const LegalPageLayout: React.FC<LegalPageLayoutProps> = ({
  eyebrow,
  title,
  summary,
  children,
  dateLabel = 'EFFECTIVE SEPTEMBER 30, 2026',
}) => {
  const t = useModeTokens();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: t.surfaces.canvas,
        color: t.text.primary,
      }}
    >
      <Box
        component="header"
        sx={{
          borderBottom: `1px solid ${t.borders.hairline}`,
          background: t.surfaces.screen,
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: 1080,
            marginX: 'auto',
            padding: { xs: '16px 20px', sm: '18px 28px' },
            display: 'flex',
            alignItems: 'center',
            gap: '20px',
          }}
        >
          <Box
            component={RouterLink}
            to="/login"
            aria-label="StudioVault home"
            sx={{ textDecoration: 'none' }}
          >
            <LogoMark size={26} />
          </Box>
          <Box
            component="nav"
            aria-label="Legal"
            sx={{
              marginLeft: 'auto',
              display: 'flex',
              gap: { xs: '14px', sm: '22px' },
              color: t.text.secondary,
              fontSize: '13px',
            }}
          >
            <Box component={RouterLink} to="/privacy" sx={legalLinkSx}>
              Privacy
            </Box>
            <Box
              component={RouterLink}
              to="/community-guidelines"
              sx={legalLinkSx}
            >
              Community
            </Box>
            <Box component={RouterLink} to="/terms" sx={legalLinkSx}>
              Terms
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        component="main"
        sx={{
          width: '100%',
          maxWidth: 820,
          marginX: 'auto',
          padding: { xs: '54px 20px 72px', sm: '72px 32px 96px' },
        }}
      >
        <Mono
          component="div"
          size={10}
          weight={600}
          letterSpacing="0.14em"
          color={t.colors.accentText}
          sx={{ marginBottom: '16px' }}
        >
          {eyebrow}
        </Mono>
        <Box
          component="h1"
          sx={{
            margin: 0,
            maxWidth: '18ch',
            fontFamily: fonts.display,
            fontSize: { xs: '38px', sm: '52px' },
            lineHeight: 1.02,
            letterSpacing: '-0.035em',
          }}
        >
          {title}
        </Box>
        <Box
          component="p"
          sx={{
            margin: '20px 0 0',
            maxWidth: '66ch',
            color: t.text.secondary,
            fontSize: '16px',
            lineHeight: 1.7,
          }}
        >
          {summary}
        </Box>
        <Mono
          component="p"
          size={10}
          sx={{ margin: '18px 0 0', color: t.text.muted }}
        >
          {dateLabel}
        </Mono>

        <Box
          component="article"
          sx={{
            marginTop: { xs: '48px', sm: '64px' },
            '& section': {
              paddingTop: '34px',
              marginTop: '34px',
              borderTop: `1px solid ${t.borders.divider}`,
            },
            '& section:first-of-type': { marginTop: 0 },
            '& h2': {
              margin: '0 0 14px',
              fontFamily: fonts.display,
              fontSize: { xs: '23px', sm: '26px' },
              lineHeight: 1.2,
              letterSpacing: '-0.02em',
            },
            '& h3': {
              margin: '24px 0 8px',
              fontSize: '15px',
              lineHeight: 1.4,
            },
            '& p': {
              margin: '10px 0',
              color: t.text.secondary,
              fontSize: '14.5px',
              lineHeight: 1.75,
            },
            '& ul': {
              margin: '12px 0',
              paddingLeft: '22px',
              color: t.text.secondary,
            },
            '& li': {
              paddingLeft: '5px',
              margin: '8px 0',
              fontSize: '14.5px',
              lineHeight: 1.7,
            },
            '& a': legalLinkSx,
            '& strong': { color: t.text.primary },
          }}
        >
          {children}
        </Box>
      </Box>

      <Box
        component="footer"
        sx={{
          borderTop: `1px solid ${t.borders.hairline}`,
          color: t.text.muted,
        }}
      >
        <Box
          sx={{
            maxWidth: 1080,
            marginX: 'auto',
            padding: '24px 20px 30px',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '12px 22px',
            fontSize: '12px',
          }}
        >
          <span>© {new Date().getFullYear()} StudioVault</span>
          <Box component={RouterLink} to="/login" sx={legalLinkSx}>
            Back to StudioVault
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LegalPageLayout;
